import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";

export type TranslatableEntity =
  | "place" | "city" | "landmark" | "carousel" | "ticket_product" | "category"
  | "city_hub_entry" | "city_hub_branding" | "menu_item";

/**
 * Live translations for one entity in the user's current language.
 *
 * Usage:
 *   const t = useEntityTranslations("place", place.id);
 *   <h1>{t("name", place.name)}</h1>
 *   <p>{t("description", place.description)}</p>
 *
 * Behaviour:
 * - If language is English → always returns the supplied English fallback.
 * - If a translation row exists for (entity, id, field, lang) → returns it.
 * - Otherwise returns the supplied English fallback (zero-downtime — old rows
 *   without translations keep working).
 *
 * Reads directly from `content_translations` via the public-read RLS policy.
 */
export function useEntityTranslations(entity: TranslatableEntity, entityId: string | undefined | null) {
  const { lang } = useLanguage();

  const enabled = !!entityId && lang !== "en";

  const { data } = useQuery({
    queryKey: ["content-translations", entity, entityId, lang],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("content_translations")
        .select("field, value")
        .eq("entity", entity)
        .eq("entity_id", entityId!)
        .eq("lang", lang);
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const row of data ?? []) map[row.field] = row.value;
      return map;
    },
  });

  return (field: string, fallback: string | null | undefined): string => {
    if (lang === "en") return fallback ?? "";
    const v = data?.[field];
    if (typeof v === "string" && v.trim().length > 0) return v;
    return fallback ?? "";
  };
}
